import React, { useEffect } from 'react'
import { FieldErrors, UseFormClearErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Input } from '@node-space/ui-components'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { BeneficiaryDetailsCurrency, BeneficiaryTransferDestination } from 'types/beneficiaries'
import { AddressDetails } from './AddressDetails'

type IntermediaryBankDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
}

export const IntermediaryBankDetails = ({
  formValues,
  errors,
  clearErrors,
  register,
  setValue,
}: IntermediaryBankDetailsProps) => {
  const { t } = useTranslation()

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, formValues?.paymentMethod, clearErrors])

  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isInternationalBankAccount =
    formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isCountryUs = formValues?.intermediaryBankCountryCode === 'US'
  const isRoutingNumberVisible = isUsd && isInternationalBankAccount && isCountryUs

  const handleInputChange =
    (field: keyof ContactFiatInstrumentFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  return (
    <Box width="full" paddingT={12} gap={12}>
      <Box paddingY={12}>
        <Input
          {...register('intermediaryBankName')}
          name="intermediaryBankName"
          testId="intermediaryBankName"
          label={t('manageBeneficiaries.intermediarysBankName')}
          placeholder={t('enterBankName')}
          errorText={errors?.intermediaryBankName?.message}
          error={!!errors?.intermediaryBankName?.message}
          onChange={handleInputChange('intermediaryBankName')}
        />
      </Box>

      <AddressDetails
        formValues={formValues}
        errors={errors}
        type="intermediaryBank"
        clearErrors={clearErrors}
        register={register}
        setValue={setValue}
      />

      <Box paddingT={12} flexSize="auto" width="full">
        <Input
          {...register('intermediaryBankCode')}
          name="intermediaryBankCode"
          testId="intermediaryBankCode"
          label={t('formFields.bic')}
          placeholder={t('formFields.placeholder.bic')}
          errorText={errors?.intermediaryBankCode?.message}
          error={!!errors?.intermediaryBankCode?.message}
          onChange={handleInputChange('intermediaryBankCode')}
        />
      </Box>

      {isRoutingNumberVisible && (
        <Box paddingT={12}>
          <Input
            {...register('intermediaryBankRoutingNumber')}
            testId="intermediaryBankRoutingNumber"
            label={t('formFields.routingNumber')}
            placeholder={t('formFields.placeholder.routingNumber')}
            errorText={errors?.intermediaryBankRoutingNumber?.message}
            error={!!errors?.intermediaryBankRoutingNumber?.message}
            value={formValues?.intermediaryBankRoutingNumber}
            onChange={handleInputChange('intermediaryBankRoutingNumber')}
          />
        </Box>
      )}
    </Box>
  )
}
